import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {Sidebar, OrderItem, SidebarStyle} from './sidebar';
import {navigate, SiteRoutes, useRoute} from '../../../lib/routes';
import {useNotFound} from '../../../lib/seo';

type Props<T> = {
	id?: string;
	items: T[];
	sidebarHeader: (item: T) => string;
	sidebarSecondary?: (item: T) => React.ReactNode | string;
	/** значки в правом верхнем углу строки */
	sidebarBadge?: (item: T) => React.ReactNode;
	route: keyof SiteRoutes;
	baseRoute: keyof SiteRoutes;
	filterFn?: (item: T, filter: string) => boolean;
	onChange?: (item: T) => void;
	sidebarStyle?: (item: T) => SidebarStyle | undefined | false;
	isFiltersSet?: boolean;
	onOpenFilters?: () => void;
	changeOrder?: (order: string) => void;
	order?: string;
	orderList?: OrderItem[];
	loading?: boolean;
	groupBy?: (item: T, index: number) => string | number;
	groupTitle?: (items: T[]) => string;
};

export function useItemsDashboard<T extends {id: string}>({
	id: propsId,
	items,
	filterFn,
	route,
	baseRoute,
	sidebarHeader,
	sidebarSecondary,
	sidebarBadge,
	onChange,
	sidebarStyle,
	isFiltersSet,
	onOpenFilters,
	order,
	changeOrder,
	orderList,
	loading,
	groupBy,
	groupTitle,
}: Props<T>) {
	const router = useRoute();
	const query = router?.query || {};
	const routeName = router?.routeName;
	const id = propsId || (query.id as string);

	const [mode, setMode] = useState<string>();
	const [showView, setShowView] = useState(!!id || false);

	const handleBack = useCallback(() => {
		navigate(baseRoute);
		setShowView(false);
	}, [baseRoute]);

	useEffect(() => {
		if (id) {
			setShowView(true);
		} else if (routeName === baseRoute) {
			setShowView(false);
		}
	}, [id, routeName, baseRoute]);

	const handleChange = useCallback(
		(item: T) => {
			if (onChange) onChange(item);
		},
		[setShowView, onChange],
	);

	const selected = useMemo(() => {
		return items.find((item) => item.id === id);
	}, [items, id]);

	// id, которого нет в полном (нефильтрованном) списке, — несуществующая страница: сервер отдаст 404
	const notFound = !!id && !loading && items.length > 0 && !selected && !isFiltersSet;
	useNotFound(notFound);

	return {
		selectedId: id,
		notFound,
		selected,
		mode,
		setMode,
		showView: typeof window !== 'undefined' ? showView : !!id,
		handleBack,
		Sidebar: (
			<Sidebar
				items={items}
				route={route}
				selected={selected}
				onChange={handleChange}
				header={sidebarHeader}
				secondary={sidebarSecondary}
				badge={sidebarBadge}
				filterFn={filterFn}
				style={sidebarStyle}
				isFiltersSet={isFiltersSet}
				onOpenFilters={onOpenFilters}
				order={order}
				orderList={orderList}
				changeOrder={changeOrder}
				loading={loading}
				groupBy={groupBy}
				groupTitle={groupTitle}
			/>
		),
	};
}
